import React, { useEffect, useMemo, useState } from "react";
import { Tag, Popover, Segmented } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { machineryApis } from "../../../api/MachineryNorms";
import ProjectScopeSelect from "../shared/ProjectScopeSelect";
import {
  MACHINERY_TEMPLATE_HEADERS,
  MACHINERY_TEMPLATE_COL_WIDTHS,
  MACHINERY_TEMPLATE_SAMPLE_ROWS,
} from "./machineryTemplate";
import { LIVE_MACHINERY_HEADS, DEFAULT_MACHINERY_HEAD } from "./machineryHeads";

// Thin wrapper over the shared ExcelUploadStageModal (upload → validating →
// preview → committing → done | failed). Only the machinery-specific pieces
// live here: template generation, preview columns, row buckets, copy strings.
//
// Machinery is a FLAT master — one rate row per Activity + Norm Code +
// Description (the unique key), no forward-fill. Existing keys are UPSERTED
// (rows are editable). Backend reportType: 'machinery-norms-upload'.

const generateMachineryTemplate = () => {
  const headers = [...MACHINERY_TEMPLATE_HEADERS];
  const sampleRows = MACHINERY_TEMPLATE_SAMPLE_ROWS.map((row) => [...row]);
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [...MACHINERY_TEMPLATE_COL_WIDTHS];

  const instructions = [
    ["Machinery Norms Upload — Instructions"],
    [""],
    ["Activity Name", "Required. The activity this rate belongs to. Include the activity code in parentheses, e.g. ADIT-3A EXCAVATION(AE-0102), so project uploads match the right project activity. Org-master uploads (projectId 0) store the text as-is."],
    ["Norm Code", "Required. The norm code the rate depends on (e.g. B3.1.1)."],
    ["Description", "Optional. Rock class / condition (e.g. Class III). Part of the unique key together with Activity Name and Norm Code."],
    ["Nearest Dump", "Optional. The dump location the lead is measured to (e.g. Hiller Peernia)."],
    ["Total Lead", "Optional. Lead distance in km, max 5 decimals (e.g. 2.50038)."],
    ["UOM", "UOM name or short code (Cum, MT...). Matched to the UOM Master; unrecognized units import with a warning."],
    ["Rate", "In ₹, max 4 decimals. The machinery rate for this Activity + Norm Code."],
    [""],
    ["Note", "Existing rows (same Activity Name + Norm Code + Description) are UPSERTED: nearest dump, total lead, UOM, rate and status are updated in place. New keys are created."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 20 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Machinery Norms");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "machinery-norms-upload-template.xlsx");
};

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      return <Tag color="green">Valid</Tag>;
    },
  },
  {
    title: "Action",
    dataIndex: "__action",
    key: "__action",
    width: 110,
    render: (value: any) => {
      if (!value) return null;
      const color =
        value === "New"
          ? "green"
          : value === "Update"
          ? "gold"
          : "default"; // No Change
      return <Tag color={color}>{value}</Tag>;
    },
  },
  { title: "Excel Row", dataIndex: "excelRow", key: "excelRow", width: 90 },
  { title: "Activity Name", dataIndex: "activityName", key: "activityName", width: 240, ellipsis: true },
  { title: "Norm Code", dataIndex: "normCode", key: "normCode", width: 110 },
  { title: "Description", dataIndex: "description", key: "description", width: 220, ellipsis: true },
  { title: "Nearest Dump", dataIndex: "nearestDump", key: "nearestDump", width: 140 },
  { title: "Total Lead", dataIndex: "totalLead", key: "totalLead", width: 100 },
  { title: "UOM", dataIndex: "units", key: "units", width: 90 },
  { title: "UOM Match", dataIndex: "unitStatus", key: "unitStatus", width: 110 },
  { title: "Rate (₹)", dataIndex: "rate", key: "rate", width: 100 },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors: string[] = Array.isArray(record?.Error)
        ? record.Error
        : record?.Error
        ? [record.Error]
        : [];
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

// Rows targeting an EXISTING key carry __action 'Update' | 'No Change' (set by
// the backend helper); 'New' is a plain insert. The Updates tab appears only
// when any upsert rows exist.
const EXISTING_ROW_ACTIONS = ["Update", "No Change"];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const updateRows = rows.filter(
    (row) => !row?.Error && EXISTING_ROW_ACTIONS.includes(row?.__action)
  );
  const validRows = rows.filter(
    (row) => !row?.Error && !EXISTING_ROW_ACTIONS.includes(row?.__action)
  );
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `New (${validRows.length})`, rows: validRows },
    ...(updateRows.length
      ? [{ key: "updates", label: `Updates (${updateRows.length})`, rows: updateRows }]
      : []),
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

// Unique key = Activity Name + Norm Code + Description.
const countNormKeys = (rows: any[]): number =>
  new Set(
    rows
      .filter((row) => !row?.Error && row?.normCode)
      .map(
        (row) =>
          `${row?.activityName ?? ""}|${row?.normCode ?? ""}|${
            row?.description ?? ""
          }`
      )
  ).size;

const machineryUploadConfig: ExcelUploadTypeConfig = {
  title: "Bulk Machinery Norms Upload",
  entityLabel: "Machinery Norms",
  templateSource: { kind: "client", generate: generateMachineryTemplate },
  startUpload: (formData) => machineryApis.uploadExcel(formData),
  // Overridden per-instance below to carry the active projectId + category.
  buildBody: (comments) => ({ comment: comments ? comments : "N/A" }),
  previewColumns,
  bucketRows,
  summaryTags: (report: UploadReportState) => (
    <Tag color="blue">{countNormKeys(report.rows)} machinery rate(s)</Tag>
  ),
  rowErrorLabel: (row) =>
    row.excelRow ? `Row ${row.excelRow}` : row.normCode || "Row",
  copy: {
    uploadIntro: (
      <p>
        You're about to import Machinery Norms. Fill the machinery Excel file
        (one rate per Activity + Norm Code + Description) accurately and
        completely, then upload it below. You'll see a full preview before
        anything is saved.
      </p>
    ),
    validatingHeading: "Validating your file…",
    validatingSub:
      "Checking rows, matching activities and matching units to the UOM Master. This normally takes a few seconds.",
    committingHeading: "Saving machinery norms…",
    committingSub:
      "Inserting new machinery rates and updating existing ones. This normally takes a few seconds.",
    commitCta: "Save Machinery Norms",
    doneHeading: "Machinery norms saved successfully",
    doneSub: (report: UploadReportState) => (
      <>
        {countNormKeys(report.rows) || ""} machinery rate(s),{" "}
        {report.successCount} row(s) written. They are now visible in the
        Machinery list.
      </>
    ),
    creationFailedNote:
      "Save failed — no rows were written (the batch is all-or-nothing).",
  },
  discardOnCancelFromPreview: false,
};

const MachineryNormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId = 0,
  category = DEFAULT_MACHINERY_HEAD,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Scope the uploaded rows to this projectId (0 = org master) + category. */
  projectId?: number;
  category?: string;
}) => {
  // The upload targets a project + category head, defaulted from the list's
  // current selection but confirmable/changeable here so the upload is
  // self-contained.
  const [scopeProjectId, setScopeProjectId] = useState<number>(projectId);
  const [scopeCategory, setScopeCategory] = useState<string>(category);

  // Re-sync to the page's selection whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setScopeProjectId(projectId);
      setScopeCategory(category);
    }
  }, [open, projectId, category]);

  const config = useMemo<ExcelUploadTypeConfig>(
    () => ({
      ...machineryUploadConfig,
      buildBody: (comments: string) => ({
        comment: comments ? comments : "N/A",
        projectId: scopeProjectId,
        category: scopeCategory,
      }),
      introSection: (
        <div className="mt-3">
          <label className="form-label mb-1">Upload into</label>
          <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
            <ProjectScopeSelect value={scopeProjectId} onChange={setScopeProjectId} />
            <Segmented
              value={scopeCategory}
              onChange={(val) => setScopeCategory(String(val))}
              options={LIVE_MACHINERY_HEADS.map((head) => ({
                label: head.label,
                value: head.key,
              }))}
            />
          </div>
          <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
            Uploaded rows are saved under this project and head.
          </div>
        </div>
      ),
    }),
    [scopeProjectId, scopeCategory]
  );
  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={scopeProjectId}
    />
  );
};

export default MachineryNormUploadModal;
